// Shared dashboard tile renderer. Used by Sales Dashboard (rep-scoped),
// Management Dashboard (firm-wide), and any future scoped variant.
//
// Two sections (other Business UI handoff, 2026-10-07):
//   • Pulse — a metric strip: white box, fine rules between cells; each
//     metric links to its filtered source list.
//   • Backlog — "First things first.": only the items waiting on the user,
//     each a row with its count (as a labelled badge) and a Review action.
//
// Each tile is a Link to its detail surface. Icons are injected per-page
// (so Sales / Management / Production can reskin if needed).
//
// Tile values can be integers (format: "count") or dollars (format:
// "currency"). Currency formats compactly: $1.2K, $48K, $1.4M. Operator
// said the Sales Dashboard needs pipeline + AR + MTD as $ values, not
// just counts.
//
// Generic on slug types so each dashboard binds its own taxonomy of
// pulse + backlog slugs (e.g. Sales uses "sales-pipeline-value" etc.,
// Management uses the firm-wide "active-orders" etc.).
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";

// Firm-wide slugs (used by Command Center home + Management Dashboard).
// Keep these here so callers that haven't migrated to per-role slugs
// continue to work.
export type PulseSlug =
  | "active-orders"
  | "in-production"
  | "open-exceptions"
  | "active-clients";

export type BacklogSlug =
  | "unreviewed-signups"
  | "pending-project-requests";

export type TileFormat = "count" | "currency";

export interface DashboardTile<S extends string> {
  slug: S;
  label: string;
  value: number;
  /** How `value` renders. Defaults to "count" when omitted. */
  format?: TileFormat;
  href: string;
  description: string;
}

type IconType = React.ComponentType<{ className?: string }>;

interface DashboardTilesProps<P extends string, B extends string> {
  pulse: Array<DashboardTile<P>>;
  backlog: Array<DashboardTile<B>>;
  pulseIcons: Record<P, IconType>;
  backlogIcons: Record<B, IconType>;
  isLoading: boolean;
  isError: boolean;
  /** Copy shown when every backlog count is zero. Different per page. */
  emptyBacklogCopy?: string;
  /** Optional panel beside "First things first." (handoff: 1.7fr / 1fr). */
  aside?: React.ReactNode;
}

/** Compact $ formatter — $1.2K, $48K, $1.4M. Falls back to $0 when value
 *  is 0 (skip the K suffix on a zero balance for readability). */
function formatCurrency(v: number): string {
  if (!Number.isFinite(v) || v === 0) return "$0";
  const abs = Math.abs(v);
  if (abs >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (abs >= 10_000) return `$${Math.round(v / 1_000).toLocaleString()}K`;
  if (abs >= 1_000) return `$${(v / 1_000).toFixed(1)}K`;
  return `$${Math.round(v).toLocaleString()}`;
}

export function formatValue(t: DashboardTile<string>): string {
  if (t.format === "currency") return formatCurrency(t.value);
  return t.value.toLocaleString();
}

export function DashboardTiles<P extends string, B extends string>({
  pulse,
  backlog,
  pulseIcons,
  backlogIcons,
  isLoading,
  isError,
  emptyBacklogCopy,
  aside,
}: DashboardTilesProps<P, B>) {
  if (isLoading) {
    // Skeleton, never zeros: a blank balance would read as "nothing owed".
    return (
      <div aria-busy="true" aria-label="Loading dashboard" className="grid grid-cols-2 gap-px overflow-hidden rounded-md border border-rule bg-rule md:grid-cols-3">
        {Array.from({ length: 6 }, (_, i) => (
          <div key={i} className="bg-white p-5">
            <div className="h-3 w-24 animate-pulse rounded bg-canvas" />
            <div className="mt-3 h-7 w-20 animate-pulse rounded bg-canvas" />
            <div className="mt-3 h-3 w-32 animate-pulse rounded bg-canvas" />
          </div>
        ))}
      </div>
    );
  }
  if (isError) {
    return (
      <div role="alert" className="rounded-md border border-risk/30 bg-risk-bg p-6 text-sm text-risk">
        We couldn't load your dashboard. Nothing has changed — refresh to try again.
      </div>
    );
  }

  const backlogSum = backlog.reduce((s, b) => s + b.value, 0);
  const showBacklog = backlogSum > 0 || emptyBacklogCopy !== undefined;
  const waiting = backlog.filter((b) => b.value > 0);

  return (
    <div className="space-y-6">
      {/* Metric strip (handoff: white box, fine rules between cells). Each
          metric opens its filtered source list. */}
      <section aria-label="Key numbers">
        <div className="grid grid-cols-1 gap-px overflow-hidden rounded-md border border-rule bg-rule sm:grid-cols-2 lg:grid-cols-3">
          {pulse.map((kpi) => {
            const Icon = pulseIcons[kpi.slug] as IconType;
            return (
              <Link
                key={kpi.slug}
                to={kpi.href}
                className="group flex flex-col bg-white p-5 transition-colors hover:bg-[#fafaf8]"
              >
                <span className="flex items-center justify-between gap-2 text-xs text-muted">
                  {kpi.label}
                  <Icon className="h-4 w-4 text-[#9a9a95] group-hover:text-ink" aria-hidden />
                </span>
                <span className="mt-1.5 text-[29px] font-medium leading-tight tracking-[-0.8px] text-ink tabular-nums">
                  {formatValue(kpi)}
                </span>
                <span className="mt-1.5 text-xs leading-snug text-muted">{kpi.description}</span>
              </Link>
            );
          })}
        </div>
      </section>

      <div className={aside ? "grid items-start gap-6 lg:grid-cols-[minmax(0,1.7fr)_minmax(240px,1fr)]" : undefined}>
      {/* "First things first." — what needs the user, each with its action. */}
      {showBacklog && (
        <section className="rounded-md border border-rule bg-white p-[22px]">
          <div className="mb-3.5 flex items-center justify-between gap-3">
            <h2 className="text-lg font-semibold tracking-[-0.4px] text-ink">First things first.</h2>
            <span className="text-xs text-muted">
              {waiting.length === 0 ? "Nothing waiting" : `${waiting.length} ${waiting.length === 1 ? "thing needs" : "things need"} you`}
            </span>
          </div>
          {backlogSum === 0 ? (
            <p className="text-sm text-muted">{emptyBacklogCopy}</p>
          ) : (
            <ul>
              {waiting.map((kpi) => {
                const Icon = backlogIcons[kpi.slug] as IconType;
                return (
                  <li key={kpi.slug} className="flex items-center justify-between gap-4 border-b border-[#e8e8e5] py-4 first:pt-0 last:border-0 last:pb-0">
                    <div className="flex min-w-0 items-start gap-3">
                      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted" aria-hidden />
                      <div className="min-w-0">
                        <p className="font-medium text-ink">
                          {kpi.label}{" "}
                          <span className="ml-1 inline-block rounded bg-warn-bg px-2 py-0.5 text-[11px] font-medium text-warn tabular-nums">
                            {formatValue(kpi)} waiting
                          </span>
                        </p>
                        <p className="mt-1 text-xs text-muted">{kpi.description}</p>
                      </div>
                    </div>
                    <Link
                      to={kpi.href}
                      className={cn(
                        "inline-flex min-h-[40px] shrink-0 items-center rounded border border-rule bg-white px-3.5 text-sm text-ink transition-colors hover:bg-canvas",
                      )}
                    >
                      Review
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      )}
      {aside}
      </div>
    </div>
  );
}
